// src/lib/fishiq/fishIQEvidence.ts
// Canonical evidence, confidence, and privacy-safe activity trace for FishIQ.
// Ranking inputs remain separate from trust scoring so generated tags cannot
// artificially inflate confidence.

import type { FishIQWaterProfile } from '../../types';

export type FishIQEvidenceStrength =
  | 'strong'
  | 'moderate'
  | 'weak'
  | 'none';

export type FishIQConfidenceLabel =
  | 'Strong local match'
  | 'Likely match'
  | 'Possible match'
  | 'Limited local data';

export type FishIQFreshness =
  | 'recent'
  | 'today'
  | 'older'
  | 'none';

export type FishIQEvidenceBucket = {
  key:
    | 'water'
    | 'regional'
    | 'seasonal'
    | 'environment'
    | 'local'
    | 'personal'
    | 'fallback';
  label: string;
  strength: FishIQEvidenceStrength;
  score: number;
  details: string[];
};

export type FishIQEvidenceTrace = {
  buckets: FishIQEvidenceBucket[];
  localSignalRows: number;
  localEvents7d: number;
  localUniqueUsers7d: number;
  localFreshness: FishIQFreshness;
  hasPersonalHistory: boolean;
  hasConfirmedWater: boolean;
  fallbackOnly: boolean;
  score: number;
  label: FishIQConfidenceLabel;
  why: string[];
};

export type FishIQLivePulseInput = {
  signals?: any[] | null;
  catchLog?: any[] | null;
  feedback?: any[] | null;
};

export type FishIQLivePulseRead = {
  activityTags: string[];
  signalCount: number;
  eventCount7d: number;
  uniqueUsers7d: number;
  freshness: FishIQFreshness;
  label: string;
};

export type FishIQConfidenceInput = {
  profile: FishIQWaterProfile;
  signalCount?: number | null;
  evidence?: FishIQEvidenceTrace | null;
};

const clean = (value: unknown) => String(value ?? '').trim();

const unique = (items: string[], limit = 8): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const item of items) {
    const value = clean(item);
    const key = value.toLowerCase();

    if (!value || seen.has(key)) continue;

    seen.add(key);
    out.push(value);

    if (out.length >= limit) break;
  }

  return out;
};

const freshnessOrder: Record<FishIQFreshness, number> = {
  none: 0,
  older: 1,
  today: 2,
  recent: 3,
};

function getSignalStats(signals: any[]) {
  let events7d = 0;
  let uniqueUsers7d = 0;
  let freshness: FishIQFreshness = 'none';

  for (const signal of signals) {
    events7d += Math.max(
      0,
      Number(signal?.count7d ?? signal?.count24h ?? 0),
    );

    uniqueUsers7d = Math.max(
      uniqueUsers7d,
      Math.max(
        0,
        Number(
          signal?.uniqueUsers7d ??
            signal?.uniqueUsers24h ??
            0,
        ),
      ),
    );

    const next = String(
      signal?.recencyBucket || '',
    ).toLowerCase() as FishIQFreshness;

    if (
      next in freshnessOrder &&
      freshnessOrder[next] > freshnessOrder[freshness]
    ) {
      freshness = next;
    }
  }

  return {
    events7d,
    uniqueUsers7d,
    freshness,
  };
}

export function buildFishIQEvidenceTrace(input: {
  profile: FishIQWaterProfile;
  rankedSpecies?: any[] | null;
  signals?: any[] | null;
  catchLog?: any[] | null;
  environment?: any | null;
}): FishIQEvidenceTrace {
  const ranked = Array.isArray(input.rankedSpecies)
    ? input.rankedSpecies
    : [];

  const signals = Array.isArray(input.signals)
    ? input.signals
    : [];

  const catchLog = Array.isArray(input.catchLog)
    ? input.catchLog
    : [];

  const stats = getSignalStats(signals);

  const waterConfirmed =
    input.profile.waterType !== 'unknown' &&
    input.profile.contextClass !== 'unknown';

  const top = ranked.slice(0, 8);

  const hasRegional = top.some(
    (item) => item?.__fishiq?.regionalSignal,
  );

  const hasSeasonal = top.some((item) => {
    const reasons =
      item?.__fishiq?.locationRank?.reasons || [];

    return reasons.some((reason: unknown) =>
      /season|dawn|dusk|night|day activity/i.test(
        clean(reason),
      ),
    );
  });

  // Personal history only supports this Area Read when one of the species
  // actually ranked into the read matches the angler's catch history. A catch
  // somewhere else in the diary must not raise confidence for unrelated fish
  // or an unrelated water. The ranker owns the species-level match.
  const hasPersonalHistory = top.some((item) => {
    const reasons =
      item?.__fishiq?.locationRank?.reasons || [];

    return reasons.some((reason: unknown) =>
      /your catch history/i.test(clean(reason)),
    );
  });

  const environmentConfidence = Math.max(
    0,
    Math.min(1, Number(input.environment?.environmentConfidence || 0)),
  );
  const environmentStrengthRaw = String(
    input.environment?.evidenceStrength || '',
  ).toLowerCase();
  const environmentAvailable = Boolean(
    input.environment &&
    (
      input.environment?.current?.available === true ||
      input.environment?.bathymetry?.available === true ||
      input.environment?.weather
    )
  );
  const environmentStrength: FishIQEvidenceStrength =
    !environmentAvailable
      ? 'none'
      : environmentStrengthRaw === 'strong'
        ? 'strong'
        : environmentStrengthRaw === 'good'
          ? 'moderate'
          : 'weak';

  let localStrength: FishIQEvidenceStrength = 'none';

  if (
    stats.events7d >= 8 &&
    stats.uniqueUsers7d >= 3
  ) {
    localStrength = 'strong';
  } else if (
    stats.events7d >= 3 &&
    stats.uniqueUsers7d >= 2
  ) {
    localStrength = 'moderate';
  } else if (signals.length > 0) {
    localStrength = 'weak';
  }

  const buckets: FishIQEvidenceBucket[] = [
    {
      key: 'water',
      label: 'Water fit',
      strength: waterConfirmed
        ? 'strong'
        : input.profile.waterType !== 'unknown'
          ? 'moderate'
          : 'weak',
      score: waterConfirmed
        ? 22
        : input.profile.waterType !== 'unknown'
          ? 14
          : 4,
      details: unique([
        input.profile.waterName || '',
        input.profile.contextReason || '',
        input.profile.waterType !== 'unknown'
          ? `${input.profile.waterType} context`
          : 'water type still being resolved',
      ]),
    },
    {
      key: 'regional',
      label: 'Regional fit',
      strength: hasRegional ? 'moderate' : 'none',
      score: hasRegional ? 16 : 0,
      details: hasRegional
        ? ['curated regional species profile']
        : [],
    },
    {
      key: 'seasonal',
      label: 'Seasonal fit',
      strength: hasSeasonal ? 'moderate' : 'weak',
      score: hasSeasonal ? 10 : 3,
      details: unique([
        input.profile.season
          ? `${input.profile.season} pattern`
          : '',
        input.profile.timeOfDay
          ? `${input.profile.timeOfDay} window`
          : '',
      ]),
    },
    {
      key: 'environment',
      label: 'Live water quality',
      strength: environmentStrength,
      score:
        environmentStrength === 'strong'
          ? 16
          : environmentStrength === 'moderate'
            ? 11
            : environmentStrength === 'weak'
              ? 5
              : 0,
      details:
        environmentStrength === 'none'
          ? []
          : unique([
              input.environment?.qualitySummary || '',
              input.environment?.current?.available === true
                ? `current evidence ${Math.round(Number(input.environment.current.confidence || 0) * 100)}%`
                : '',
              input.environment?.bathymetry?.available === true
                ? `depth evidence ${Math.round(Number(input.environment.bathymetry.confidence || 0) * 100)}%`
                : '',
            ]),
    },
    {
      key: 'local',
      label: 'Local observations',
      strength: localStrength,
      score:
        localStrength === 'strong'
          ? 34
          : localStrength === 'moderate'
            ? 24
            : localStrength === 'weak'
              ? 8
              : 0,
      details:
        localStrength === 'none'
          ? []
          : unique([
              `${stats.events7d} delayed observations in 7 days`,
              `${stats.uniqueUsers7d} contributing anglers`,
              stats.freshness !== 'none'
                ? `${stats.freshness} activity bucket`
                : '',
            ]),
    },
    {
      key: 'personal',
      label: 'Your catch history',
      strength: hasPersonalHistory
        ? 'moderate'
        : 'none',
      score: hasPersonalHistory ? 10 : 0,
      details: hasPersonalHistory
        ? ['personal catch memory supports this read']
        : [],
    },
    {
      key: 'fallback',
      label: 'Guide knowledge',
      strength: ranked.length ? 'weak' : 'none',
      score: ranked.length ? 4 : 0,
      details: ranked.length
        ? ['species guide and habitat knowledge']
        : [],
    },
  ];

  const fallbackOnly =
    localStrength === 'none' &&
    !hasPersonalHistory;

  let score = buckets.reduce(
    (sum, bucket) => sum + bucket.score,
    0,
  );

  // Editorial/regional knowledge can rank species,
  // but never claim a strong local read.
  if (fallbackOnly) {
    score = Math.min(
      score,
      hasRegional && waterConfirmed ? 69 : 54,
    );
  } else if (localStrength === 'weak') {
    score = Math.min(score, 64);
  } else if (localStrength === 'moderate') {
    score = Math.min(score, 84);
  } else {
    score = Math.min(score, 94);
  }

  score = Math.max(0, Math.round(score));

  let label: FishIQConfidenceLabel =
    'Limited local data';

  if (
    localStrength === 'strong' &&
    waterConfirmed &&
    score >= 78
  ) {
    label = 'Strong local match';
  } else if (
    (localStrength === 'moderate' ||
      hasPersonalHistory) &&
    waterConfirmed &&
    score >= 62
  ) {
    label = 'Likely match';
  } else if (score >= 40) {
    label = 'Possible match';
  }

  const why = unique([
    waterConfirmed
      ? `${input.profile.contextClass || input.profile.waterType} water context is resolved`
      : 'water context is still being resolved',

    localStrength === 'strong' ||
    localStrength === 'moderate'
      ? `${stats.events7d} privacy-delayed local observations from at least ${stats.uniqueUsers7d} anglers`
      : localStrength === 'weak'
        ? 'a small local observation sample is available'
        : 'no qualifying local observation sample is available yet',

    hasRegional
      ? 'curated regional habitat knowledge supports the ranking'
      : '',

    hasSeasonal
      ? 'season and time-of-day fit are included'
      : '',

    environmentAvailable
      ? `live water evidence is ${environmentStrengthRaw || 'limited'} at ${Math.round(environmentConfidence * 100)}% confidence`
      : 'live water evidence is unavailable for this read',

    hasPersonalHistory
      ? 'your own catch history adds personal context'
      : '',
  ]);

  return {
    buckets,
    localSignalRows: signals.length,
    localEvents7d: stats.events7d,
    localUniqueUsers7d: stats.uniqueUsers7d,
    localFreshness: stats.freshness,
    hasPersonalHistory,
    hasConfirmedWater: waterConfirmed,
    fallbackOnly,
    score,
    label,
    why,
  };
}

export function buildFishIQLivePulse(
  input: FishIQLivePulseInput = {},
): FishIQLivePulseRead {
  const signals = Array.isArray(input.signals)
    ? input.signals
    : [];

  const catchLog = Array.isArray(input.catchLog)
    ? input.catchLog
    : [];

  const feedback = Array.isArray(input.feedback)
    ? input.feedback
    : [];

  const stats = getSignalStats(signals);

  const activityTags: string[] = [];

  if (
    stats.events7d >= 8 &&
    stats.uniqueUsers7d >= 3
  ) {
    activityTags.push(
      'strong privacy-safe local pulse',
    );
  } else if (
    stats.events7d >= 3 &&
    stats.uniqueUsers7d >= 2
  ) {
    activityTags.push('qualifying local activity');
  } else if (signals.length || feedback.length) {
    activityTags.push('limited local activity');
  }

  if (catchLog.length) {
    activityTags.push(
      'personal catch memory available',
    );
  }

  if (!activityTags.length) {
    activityTags.push(
      'forecast and guide context only',
    );
  }

  return {
    activityTags,
    signalCount: signals.length,
    eventCount7d: stats.events7d,
    uniqueUsers7d: stats.uniqueUsers7d,
    freshness: stats.freshness,
    label:
      stats.events7d >= 3 &&
      stats.uniqueUsers7d >= 2
        ? 'Local Activity Pulse'
        : 'Water Forecast',
  };
}

export function buildFishIQConfidence(
  input: FishIQConfidenceInput,
): number {
  if (input.evidence) {
    return input.evidence.score;
  }

  // Compatibility fallback only.
  // Real FishIQ Area Reads should provide evidence.
  let score = 18;

  if (input.profile.waterType !== 'unknown') {
    score += 14;
  }

  if (
    input.profile.contextClass &&
    input.profile.contextClass !== 'unknown'
  ) {
    score += 8;
  }

  if (Number(input.signalCount || 0) > 0) {
    score += Math.min(
      18,
      Number(input.signalCount || 0) * 3,
    );
  }

  return Math.max(
    0,
    Math.min(58, Math.round(score)),
  );
}